import { Injectable } from '@angular/core';
import { DynamicField, FieldType } from '../models/dynamic-field';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicFieldsService {

  // Cache para evitar múltiples llamadas a la API
  private fieldsCache: Map<number, DynamicField[]> = new Map();
  private fieldValuesCache: Map<string, any[]> = new Map();
  private loadingPromises: Map<number, Promise<DynamicField[]>> = new Map();

  constructor(private api: ApiService) { }

  getFieldsForService(serviceId: number): Promise<DynamicField[]> {
    if (this.fieldsCache.has(serviceId)) {
      return Promise.resolve(this.fieldsCache.get(serviceId)!);
    }

    if (this.loadingPromises.has(serviceId)) {
      return this.loadingPromises.get(serviceId)!;
    }

    const promise = this.api.serviceDynamicFields.get(serviceId).promise()
      .then((response: any) => {

        if (!Array.isArray(response)) {
          console.error('Response is not an array:', response);
          return [];
        }

        const fields = response.map((field: any) => this.mapBackendFieldToFrontend(field));

        this.fieldsCache.set(serviceId, fields);

        this.loadingPromises.delete(serviceId);

        return fields;
      })
      .catch((error) => {
        console.error('Error al obtener campos dinámicos:', error);

        this.loadingPromises.delete(serviceId);

        return [];
      });

    this.loadingPromises.set(serviceId, promise);

    return promise;
  }

  saveFieldsForService(serviceId: number, fields: DynamicField[]): Promise<void> {
    const savePromises = fields.map(field => {
      if (field.id && field.id.startsWith('field_')) {
        return this.createField(serviceId, field);
      } else {
        return this.updateField(field);
      }
    });

    return Promise.all(savePromises).then(() => {
    }).catch((error) => {
      console.error('Error al guardar campos:', error);
      throw error;
    });
  }

  removeFieldsForService(serviceId: number): Promise<void> {
    return this.getFieldsForService(serviceId).then(fields => {
      const deletePromises = fields.map(field => this.deleteField(field.id));
      return Promise.all(deletePromises).then(() => {});
    });
  }

  createField(serviceId: number, field: DynamicField): Promise<DynamicField> {
    const backendField = this.mapFrontendFieldToBackend(field, serviceId);
    return this.api.serviceDynamicFields.create(backendField).promise()
      .then((response: any) => {
        this.invalidateFieldsCache(serviceId);
        return this.mapBackendFieldToFrontend(response);
      });
  }

  updateField(field: DynamicField): Promise<DynamicField> {
    const backendField = this.mapFrontendFieldToBackend(field);
    return this.api.serviceDynamicFields.update(backendField, field.id).promise()
      .then((response: any) => {
        if (field.service_id) {
          this.invalidateFieldsCache(field.service_id);
        }
        return this.mapBackendFieldToFrontend(response);
      });
  }

  deleteField(fieldId: string, serviceId?: number): Promise<void> {
    return this.api.serviceDynamicFields.delete(fieldId).promise()
      .then(() => {
        if (serviceId) {
          this.invalidateFieldsCache(serviceId);
        } else {
          this.invalidateAllFieldsCache();
        }
      });
  }

  reorderFields(serviceId: number, fieldOrders: {field_id: string, order: number}[]): Promise<void> {
    return this.api.serviceDynamicFields.reorder({service_id: serviceId, field_orders: fieldOrders}).promise()
      .then(() => {
        this.invalidateFieldsCache(serviceId);
      });
  }

  getFieldValues(serviceId: number, providerCoverageId: number): Promise<any[]> {
    let params = {
      "service_id": serviceId,
      "provider_coverage_id": providerCoverageId
    }
    return this.api.serviceCoverageFieldValues.get(params).promise()
      .then((response: any) => {

        if (!Array.isArray(response)) {
          console.error('Field values response is not an array:', response);
          return [];
        }

        return response;
      })
      .catch((error) => {
        console.error('Error al obtener valores de campos:', error);
        return [];
      });
  }

  saveFieldValues(serviceId: number, providerCoverageId: number, fieldValues: { [fieldId: string]: any }): Promise<any[]> {
    const fieldValuesArray = Object.keys(fieldValues).map(fieldId => ({
      field_id: parseInt(fieldId, 10),
      field_value: fieldValues[fieldId]
    }));

    const payload = {
      service_id: serviceId,
      provider_coverage_id: providerCoverageId,
      field_values: fieldValuesArray
    };

    return this.api.serviceCoverageFieldValues.createOrUpdate(payload).promise()
      .then((response: any) => {
        return response;
      })
      .catch((error) => {
        console.error('Error al guardar valores de campos:', error);
        throw error;
      });
  }

  getFieldValuesContext(serviceId: number, providerCoverageId: number): Promise<{ [fieldName: string]: any }> {
    return this.api.serviceCoverageFieldValues.getContext(serviceId, providerCoverageId).promise()
      .then((response: any) => {
        return response || {};
      })
      .catch((error) => {
        console.error('Error al obtener contexto de valores:', error);
        return {};
      });
  }

  invalidateFieldsCache(serviceId: number): void {
    this.fieldsCache.delete(serviceId);
    this.loadingPromises.delete(serviceId);
  }

  invalidateAllFieldsCache(): void {
    this.fieldsCache.clear();
    this.loadingPromises.clear();
  }

  invalidateFieldValuesCache(serviceId: number, providerCoverageId: number): void {
    const cacheKey = `${serviceId}_${providerCoverageId}`;
    this.fieldValuesCache.delete(cacheKey);
  }

  generateFieldId(): string {
    return 'field_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  createEmptyField(): DynamicField {
    return {
      id: this.generateFieldId(),
      name: '',
      type: FieldType.TEXT,
      required: false,
      options: [],
      defaultValue: null,
      order: 0
    };
  }

  validateField(field: DynamicField): boolean {
    if (!field.name || !field.name.trim()) {
      return false;
    }

    if (field.type === FieldType.COMBO && (!field.options || field.options.length === 0)) {
      return false;
    }

    return true;
  }

  getServicesWithFields(): Promise<number[]> {
    // Este método podría implementarse en el backend si es necesario
    // Por ahora retornamos un array vacío
    return Promise.resolve([]);
  }

  private mapBackendFieldToFrontend(backendField: any): DynamicField {
    return {
      id: backendField.id.toString(),
      name: backendField.field_name,
      type: backendField.field_type as FieldType,
      is_fixed: backendField.is_fixed,
      required: backendField.is_required,
      options: backendField.options || [],
      defaultValue: backendField.default_value,
      order: backendField.field_order
    };
  }

  private mapFrontendFieldToBackend(frontendField: DynamicField, serviceId?: number): any {
    return {
      service_id: serviceId || frontendField.service_id,
      field_name: frontendField.name,
      field_type: frontendField.type,
      is_required: frontendField.required || false,
      default_value: frontendField.defaultValue,
      options: frontendField.options,
      field_order: frontendField.order || 0
    };
  }
}